import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, numberFormat } from 'utils/app.utils'

/**COMPONENT */
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import ExportToExcel from './ExportToExcel'

/**STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import HeaderCard from 'component/CardCustom/HeaderCard'
import Loading from 'component/Loading'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import { UseSnCode } from 'utils/useSnCode'
import AdvanceBalanceList from './BalanceList'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function AdvanceBalance() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [search, setSearch] = useState<string>('')

  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [fetch, setFetch] = useState(false)
  const [rows, setRows] = useState<any[]>([])
  const [rowsExport, setRowsExport] = useState<any[]>([])
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(true)
  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }
    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'ADVANCE_BALANCE') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  const fetchDF = useCallback(async () => {
    let condition: any = {
      page,
      pageLimit,
      branchId,
      search
    }

    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportAdvanceBalance(condition)
      .then(({ headers, data }) => {
        setRows(data.rows)
        setRowsExport(data.rowsExport)
        setRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
      })
    setFetch(false)
  }, [page, pageLimit, sortBy, sortType, fetch])

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.ADVANCE_BALANCE`)}`,
    sheetName: `${t(`REPORT.REPORT_NAME.ADVANCE_BALANCE`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangePage = (val: number) => setPage(val)

  const exportExcel = async () => {
    setLoading(true);
    try {
      const branchName = branchId === 0
        ? t('REPORT.FILTER.ALL')
        : _.get(_.find(branches, { branchId }), 'branchName', '');
      await ExportToExcel({
        data: rowsExport,
        branchName: branchName,
        t: t,
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDF()
  }, [fetchDF])


  return (
    <Box>
      <Loading show={loading} />
      <HeaderCard text={t(`REPORT.REPORT_NAME.ADVANCE_BALANCE`)} />
      <Box padding="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={4} xl={3}>
            <TextInput
              label={t('INPUT.SEARCH_PATIENT')}
              value={search}
              fullWidth
              onChange={(event) => setSearch(event.target.value)}
              inputProps={{
                value: search,
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={3}>
            <SelectInput

              onChange={(e) => {
                setBranchId(e.target.value)
                setPage(1)
              }}
              options={_.map(branches, (item) => ({ value: item.branchId, name: item.branchName }))}
              value={branchId}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={6}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <MainButton
                title={t('INPUT.SEARCH')}
                onClick={() => {
                  setFetch(true)
                  setPage(1)
                  setLoading(true)
                }}
                type={'primary'}
                variant={'contained'}
                style={{ width: '120px' }}
              />
              <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                if (e === 'Excel') {
                  exportExcel();
                } else if (e === 'PDF') {
                  const style = document.createElement("style");
                  style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                  document.head.appendChild(style);
                  window.print();
                  document.head.removeChild(style);

                }
              }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
            </Box>
          </Grid>
        </Grid>
        <Box marginTop="16px">
          <AdvanceBalanceList
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={rows}
            dataExport={rowsExport}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            setSortType={setSortType}
            setSortBy={setSortBy}
            componentRef={componentRef}
            branchName={_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}
          />
        </Box>
      </Box>
      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t(`REPORT.REPORT_NAME.ADVANCE_BALANCE`)}</Typography>
        <Typography fontSize="10px" fontWeight={400}>{t('REPORT.TABLE.DECREASE.BRANCH')}: {_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}</Typography>
        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.DF.#NO')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.CN_NUMBER')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.BRANCH_CN_NUMBER')}</th>
              {snType && <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.SN_CODE')}</th>}
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_NAME')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'end' }}>{t('REPORT.TABLE.ADVANCE.ADVANCE_BALANCE')}</th>
            </tr>
          </thead>
          <tbody>
            {rowsExport.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top', width: '85px' }}>{data?.cnNumber}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top', width: '100px' }}>{data?.branchCnNumber}</td>
                {snType && <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top', width: '100px' }}>{data?.snCode}</td>}
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.patientFullname}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'end', verticalAlign: 'top' }}>{numberFormat(data.advanceBalance, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <br></br>
      </Box>
    </Box>
  )
}
